import AdminPage from '@/pages/admin';
import AdminSettingsPage from '@/pages/admin/settings';
import AdminBuilderQueuePage from '@/pages/admin/builder-queue';
import AdminCardFillPage from '@/pages/admin/card-fill';
import AdminSenderProxiesPage from '@/pages/admin/sender/proxies';
import AdminStatsPage from '@/pages/admin/stats';
import AdminUserDetailPage from '@/pages/admin/users/detail';
import AdminUserSenderRunDetailPage from '@/pages/admin/users/detail/sender/run-detail';
import AdminUsersPage from '@/pages/admin/users';
import AuthPage from '@/pages/auth';
import ApkPage from '@/pages/apk';
import ApkBuilderPage from '@/pages/apk/builder';
import ApkBuildsPage from '@/pages/apk/builds';
import ApkDomainsPage from '@/pages/apk/domains';
import ApkDomainDetailPage from '@/pages/apk/domains/detail';
import LandingsPage from '@/pages/landings';
import LandingsNotificationPage from '@/pages/landings/notification';
import LandingsMetaPixelsPage from '@/pages/landings/meta-pixels';
import LandingsTemplatesPage from '@/pages/landings/templates';
import LandingsTelegramAuthCustomPage from '@/pages/landings/templates/telegram-auth-custom';
import TelegramAuthConstructorWizardPage from '@/pages/landings/templates/telegram-auth-constructor-wizard';
import LandingsDomainsPage from '@/pages/landings/domains';
import LandingDomainDetailPage from '@/pages/landings/domains/detail';
import LandingsSitesPage from '@/pages/landings/sites';
import LandingsLinksPage from '@/pages/landings/links';
import LandingsEventsPage from '@/pages/landings/events';
import { senderChildRoutes } from '@/app/sender-route-tree';
import SenderEvaShell from '@/pages/sender/eva/sender-eva-shell';
import SenderLegacyBookmarkRedirect from '@/pages/sender/legacy-bookmark-redirect';
import SenderLegacyGate from '@/pages/sender/sender-legacy-gate';
import ClientPage from '@/pages/client';
import ClientsPage from '@/pages/clients';
import MyClientPage from '@/pages/my-client';
import MyClientsPage from '@/pages/my-clients';
import ProfilePage from '@/pages/profile';
import PartnerWorkersPage from '@/pages/workers';
import PartnerWorkerDetailPage from '@/pages/workers/detail';
import PartnerWorkerSenderRunDetailPage from '@/pages/workers/detail/run-detail';
import ProtectedRoute from '@/components/protected-route';
import AppShell from '@/layouts/app-shell';
import { ROUTES } from '@/app/routes';
import { createRoot } from 'react-dom/client';
import { Navigate, createBrowserRouter, RouterProvider, useParams } from 'react-router';
import App from './App';
import { logPanelBuildId } from '@/lib/panel-build-id';
import './index.css';

logPanelBuildId();

// #region agent log
fetch('http://127.0.0.1:7562/ingest/093913fe-69ef-4d28-86d1-82d97273f4d6',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c9b202'},body:JSON.stringify({sessionId:'c9b202',hypothesisId:'A',location:'main.tsx:boot',message:'panel entry booted',data:{href:location.href,pathname:location.pathname,port:location.port,script:'main.tsx'},timestamp:Date.now()})}).catch(()=>{});
// #endregion

function LegacyDomainDetailRedirect() {
	const { id } = useParams<{ id: string }>();
	return <Navigate to={`${ROUTES.apkDomain}${id}`} replace />;
}

export const router = createBrowserRouter([
	{
		path: '/',
		Component: App,
		children: [
			{
				path: ROUTES.auth,
				Component: AuthPage,
			},
			{
				Component: AppShell,
				children: [
					{
						path: ROUTES.myClients,
						Component: MyClientsPage,
					},
					{
						path: ROUTES.clients,
						Component: () => (
							<ProtectedRoute roles={['carder', 'admin', 'partner']}>
								<ClientsPage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.apk,
						Component: ApkPage,
					},
					{
						path: ROUTES.apkBuilder,
						Component: ApkBuilderPage,
					},
					{
						path: ROUTES.apkDomains,
						Component: ApkDomainsPage,
					},
					{
						path: `${ROUTES.apkDomain}:id`,
						Component: ApkDomainDetailPage,
					},
					{
						path: ROUTES.apkBuilds,
						Component: ApkBuildsPage,
					},
					{
						path: ROUTES.builder,
						element: <Navigate to={ROUTES.apkBuilder} replace />,
					},
					{
						path: ROUTES.domains,
						element: <Navigate to={ROUTES.apkDomains} replace />,
					},
					{
						path: `${ROUTES.domain}:id`,
						Component: LegacyDomainDetailRedirect,
					},
					{
						path: `${ROUTES.myClient}:id`,
						Component: MyClientPage,
					},
					{
						path: ROUTES.client + ':id',
						Component: () => (
							<ProtectedRoute roles={['carder', 'admin', 'partner']}>
								<ClientPage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.admin,
						Component: () => (
							<ProtectedRoute roles={['admin']}>
								<AdminPage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.adminUsers,
						Component: () => (
							<ProtectedRoute roles={['admin']}>
								<AdminUsersPage />
							</ProtectedRoute>
						),
					},
					{
						path: `${ROUTES.adminUser}:profileId/sender/runs/:runId`,
						Component: () => (
							<ProtectedRoute roles={['admin']}>
								<AdminUserSenderRunDetailPage />
							</ProtectedRoute>
						),
					},
					{
						path: `${ROUTES.adminUser}:profileId`,
						Component: () => (
							<ProtectedRoute roles={['admin']}>
								<AdminUserDetailPage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.adminStats,
						Component: () => (
							<ProtectedRoute roles={['admin']}>
								<AdminStatsPage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.adminSettings,
						Component: () => (
							<ProtectedRoute roles={['admin']}>
								<AdminSettingsPage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.adminSenderProxies,
						Component: () => (
							<ProtectedRoute roles={['admin']}>
								<AdminSenderProxiesPage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.adminBuilderQueue,
						Component: () => (
							<ProtectedRoute roles={['admin']}>
								<AdminBuilderQueuePage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.adminCardFill,
						Component: () => (
							<ProtectedRoute roles={['admin']}>
								<AdminCardFillPage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.profile,
						Component: ProfilePage,
					},
					{
						path: ROUTES.workers,
						Component: () => (
							<ProtectedRoute roles={['partner']}>
								<PartnerWorkersPage />
							</ProtectedRoute>
						),
					},
					{
						path: `${ROUTES.worker}:profileId/sender/runs/:runId`,
						Component: () => (
							<ProtectedRoute roles={['partner']}>
								<PartnerWorkerSenderRunDetailPage />
							</ProtectedRoute>
						),
					},
					{
						path: `${ROUTES.worker}:profileId`,
						Component: () => (
							<ProtectedRoute roles={['partner']}>
								<PartnerWorkerDetailPage />
							</ProtectedRoute>
						),
					},
					{
						path: ROUTES.landings,
						Component: LandingsPage,
					},
					{
						path: ROUTES.landingsNotification,
						Component: LandingsNotificationPage,
					},
					{
						path: ROUTES.landingsMetaPixels,
						Component: LandingsMetaPixelsPage,
					},
					{
						path: ROUTES.landingsTemplates,
						Component: LandingsTemplatesPage,
					},
					{
						path: ROUTES.landingsTelegramAuthTemplates,
						Component: LandingsTelegramAuthCustomPage,
					},
					{
						path: ROUTES.landingsTelegramAuthConstructor,
						Component: TelegramAuthConstructorWizardPage,
					},
					{
						path: `${ROUTES.landingsTelegramAuthConstructor}/:templateId`,
						Component: TelegramAuthConstructorWizardPage,
					},
					{
						path: ROUTES.landingsDomains,
						Component: LandingsDomainsPage,
					},
					{
						path: `${ROUTES.landingsDomain}:id`,
						Component: LandingDomainDetailPage,
					},
					{
						path: ROUTES.landingsSites,
						Component: LandingsSitesPage,
					},
					{
						path: ROUTES.landingsLinks,
						Component: LandingsLinksPage,
					},
					{
						path: ROUTES.landingsEvents,
						Component: LandingsEventsPage,
					},
					{
						path: ROUTES.sender,
						Component: SenderLegacyGate,
						children: [
							{
								Component: SenderEvaShell,
								children: [
									...senderChildRoutes,
									{ path: 'runs/new', Component: SenderLegacyBookmarkRedirect },
									{ path: 'campaigns', Component: SenderLegacyBookmarkRedirect },
									{ path: 'campaigns/*', Component: SenderLegacyBookmarkRedirect },
									{ path: 'builds', Component: SenderLegacyBookmarkRedirect },
								],
							},
						],
					},
				],
			},
			{
				path: '/home',
				element: <Navigate to={ROUTES.profile} replace />,
			},
			{
				path: '/settings',
				element: <Navigate to={ROUTES.profile} replace />,
			},
			{
				index: true,
				element: <Navigate to={ROUTES.profile} replace />,
			},
		],
	},
]);

createRoot(document.getElementById('root')!).render(<RouterProvider router={router} />);
